import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import Notification from '../util/Notification';
import ConfirmationModal from '../util/ConfirmationModal';

import UserTable from './UserTable';
import UserGroup from './UserGroup';
import Statistics from './Statistics';
import { mislecAPI } from '../../util/axios';

function Users({ user, updateUser }) {
    const [users, setUsers] = React.useState(null);
    const [sortedUsers, setSortedUsers] = React.useState(null);
    const [open, setOpen] = React.useState(false);
    const [message, setMessage] = React.useState(null);
    const [severity, setSeverity] = React.useState(null);
    const [userId, setUserId] = React.useState(null);
    const [menuAnchor, setMenuAnchor] = React.useState(null);
    const [deleteUserModal, setDeleteUserModal] = React.useState(false);
    const menu = Boolean(menuAnchor);

    const { t } = useTranslation();
    const navigate = useNavigate();

    React.useEffect(() => {
        if (users) return;
        if (user.role !== 'admin') {
            setMessage(t('users.access'));
            setSeverity('error');
            setOpen(true);
            setTimeout(() => {
                navigate('/');
            }, 2500);
            return;
        }

        fetchUsers();
    });

    const fetchUsers = () => {
        //  Fetch users from the server
        mislecAPI.get(`/api/users`)
            .then(res => {
                setUsers(res.data.users);
                setSortedUsers(res.data.users);
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    const changeRole = (e, user) => {
        //  Data preparation
        const data = { ...user, role: e.target.value };

        //  Send data to the server
        mislecAPI.put(`/api/users/role/${user._id}`, data)
            .then(res => {
                //  Notify user of success
                setMessage(t(res.data.detail));
                setSeverity('success');
                setOpen(true);

                //  Update user data
                fetchUsers();
                if (user.email === sessionStorage.getItem('mISLec_user_email'))
                    updateUser(res.data.user);
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    const deleteUser = () => {
        if (!userId) return;

        //  Remove user from the server
        mislecAPI.delete(`/api/users/${userId}`)
            .then(res => {
                //  Notify user of successful deletion
                setUserId(null);
                fetchUsers();
                setDeleteUserModal(false);
                setMessage(t(res.data.detail));
                setSeverity('success');
                setOpen(true);
            })
            .catch(error => {
                //  Notify user of error
                setUserId(null);
                setDeleteUserModal(false);
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    return (
        <>
            <Container>
                <Stack direction='row' alignItems='center' justifyContent='space-between' mb={2}>
                    <Typography variant='h4'>{t('users.title')}</Typography>
                </Stack>
                {users &&
                    <Statistics users={users} />
                }
                {sortedUsers &&
                    <Paper elevation={2}>
                        <Box
                            sx={{
                                p: 2,
                                pb: 0,
                                backgroundColor: 'white',
                                borderRadius: '16px'
                            }}
                        >
                            <UserTable
                                user={user}
                                users={users}
                                sortedUsers={sortedUsers}
                                setSortedUsers={setSortedUsers}
                                userId={userId}
                                setUserId={setUserId}
                                menuAnchor={menuAnchor}
                                setMenuAnchor={setMenuAnchor}
                                setDeleteUserModal={setDeleteUserModal}
                                changeRole={changeRole}
                                menu={menu}
                            />
                        </Box>
                    </Paper>
                }
                <Paper elevation={2} sx={{ mt: { xs: 4, md: 3 } }}>
                    <Box
                        sx={{
                            p: 2,
                            pb: 0,
                            backgroundColor: 'white',
                            borderRadius: '16px'
                        }}
                    >
                        <UserGroup
                            setOpen={setOpen}
                            setMessage={setMessage}
                            setSeverity={setSeverity}
                        />
                    </Box>
                </Paper>
                {!users && (
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '700px' }}>
                            <CircularProgress />
                        </Box>
                    </Grid>
                )}
            </Container>
            <Notification open={open} message={message} severity={severity} onClose={() => setOpen(false)} />
            <ConfirmationModal
                open={deleteUserModal}
                onClose={() => setDeleteUserModal(false)}
                title={t('users.modals.removeUser.title')}
                description={t('users.modals.removeUser.description')}
                type='remove'
                confirmationText={t('users.modals.removeUser.confirmation')}
                confirm={deleteUser}
            />
        </>
    );
}

export default Users;